import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  listRoles,
  listModules,
  listPermissions,
  getRolePermissionsMatrix,
  bulkSetRolePermissions,
} from "../../../../utils/services/control/rbacAdminService";
import ExportExcelButton from "../../../../components/Basic/ExportExcelButton";
import useSnack from "../../hooks/useSnack";

/**
 * Modelo simplificado de acceso por rol (mientras la app no diferencie entre
 * Read / Create / Update / Delete a nivel de UI).
 *
 * Por cada módulo, el admin marca un único checkbox "tiene acceso?". En el
 * backend este flag se traduce a:
 *   - ON: otorgar TODOS los permisos activos del catálogo a ese rol×módulo.
 *   - OFF: revocar todos los permisos del rol×módulo.
 *
 * El bulk endpoint maneja insert/reactivate/revoke de forma idempotente, así
 * que basta con enviar la lista completa de pares (moduleId, permissionId)
 * deseados — el SP calcula la diferencia.
 *
 * Cuando la app empiece a verificar permisos por acción concreta, este
 * componente puede crecer a la grilla módulo×permiso sin tocar el backend.
 */
const RolePermissionsMatrixTab = () => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  // Set de moduleIds que tienen al menos un permiso activo.
  const [accessSet, setAccessSet] = useState(new Set());
  const [initialAccessSet, setInitialAccessSet] = useState(new Set());
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [saving, setSaving] = useState(false);

  const { snack, showSnack, closeSnack } = useSnack();

  // Cargar catálogos (solo activos)
  useEffect(() => {
    const loadCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const [r, m, p] = await Promise.all([
          listRoles(1),
          listModules(1),
          listPermissions(1),
        ]);
        setRoles(Array.isArray(r) ? r : []);
        setModules(Array.isArray(m) ? m : []);
        setPermissions(Array.isArray(p) ? p : []);
      } catch (err) {
        console.error("Error loading catalogs:", err);
      } finally {
        setLoadingCatalogs(false);
      }
    };
    loadCatalogs();
  }, []);

  const loadMatrix = useCallback(async (roleId) => {
    if (!roleId) return;
    setLoadingMatrix(true);
    try {
      const res = await getRolePermissionsMatrix(roleId);
      if (res?.result !== 0) {
        showSnack(res?.message || "Error", "error");
        setSelectedRole(null);
        setAccessSet(new Set());
        setInitialAccessSet(new Set());
        return;
      }
      setSelectedRole(res.role);
      // Un módulo cuenta como "tiene acceso" si tiene al menos un permiso asignado.
      const access = new Set();
      (res.matrix || []).forEach((cell) => {
        if (cell.assigned) access.add(cell.moduleId);
      });
      setAccessSet(new Set(access));
      setInitialAccessSet(new Set(access));
    } catch (err) {
      showSnack("Error loading matrix", "error");
    } finally {
      setLoadingMatrix(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRoleId) loadMatrix(selectedRoleId);
  }, [selectedRoleId, loadMatrix]);

  const isInactive = selectedRole && selectedRole.status !== 1;

  const toggleModule = (moduleId) => {
    if (isInactive) return;
    setAccessSet((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  const toggleAll = () => {
    if (isInactive) return;
    setAccessSet((prev) => {
      const allOn = modules.every((m) => prev.has(m.moduleId));
      if (allOn) return new Set();
      return new Set(modules.map((m) => m.moduleId));
    });
  };

  const allChecked = modules.length > 0 && modules.every((m) => accessSet.has(m.moduleId));
  const someChecked = !allChecked && modules.some((m) => accessSet.has(m.moduleId));

  const exportRows = useMemo(
    () =>
      modules.map((m) => ({
        moduleId: m.moduleId,
        moduleName: m.moduleName,
        moduleKey: m.moduleKey,
        description: m.description || "",
        access: accessSet.has(m.moduleId) ? "Yes" : "No",
      })),
    [modules, accessSet]
  );

  const exportColumns = [
    { field: "moduleId", headerName: "Module ID" },
    { field: "moduleName", headerName: "Module Name" },
    { field: "moduleKey", headerName: "Module Key" },
    { field: "description", headerName: "Description" },
    { field: "access", headerName: "Access" },
  ];

  const exportFilename = selectedRole
    ? `role_access_${selectedRole.roleKey || selectedRole.roleId}`
    : "role_access";

  const dirty = useMemo(() => {
    if (accessSet.size !== initialAccessSet.size) return true;
    for (const id of accessSet) if (!initialAccessSet.has(id)) return true;
    return false;
  }, [accessSet, initialAccessSet]);

  const handleSave = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      // Para cada módulo en accessSet, granteamos TODOS los permisos activos
      // del catálogo. Para los demás módulos, no enviamos pares — el SP los
      // revocará automáticamente.
      const list = [];
      accessSet.forEach((moduleId) => {
        permissions.forEach((p) => {
          list.push({ moduleId, permissionId: p.permissionId });
        });
      });
      const res = await bulkSetRolePermissions({
        roleId: Number(selectedRoleId),
        permissions: list,
      });
      if (res?.result !== 0) {
        showSnack(res?.message || "Error saving role permissions", "error");
        return;
      }
      const total = res.totalChanges ?? 0;
      if (total === 0) {
        showSnack("No changes — the role permissions are unchanged.", "info");
      } else {
        showSnack(
          `Role access updated successfully — granted: ${res.inserted ?? 0}, reactivated: ${res.reactivated ?? 0}, revoked: ${res.revoked ?? 0}`,
          "success"
        );
      }
      loadMatrix(selectedRoleId);
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error saving role permissions",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Role Access by Module
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick a role and check the modules it can access. For now, having access
        to a module means full permissions on it (read, create, update, delete).
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Select Role</InputLabel>
          <Select
            value={selectedRoleId}
            label="Select Role"
            onChange={(e) => setSelectedRoleId(e.target.value)}
            disabled={loadingCatalogs}
          >
            {roles.map((r) => (
              <MenuItem key={r.roleId} value={r.roleId}>
                {r.roleName}
                {r.isSystem ? " ⭐" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedRole && (
          <Chip
            label={selectedRole.roleKey}
            size="small"
            variant="outlined"
            sx={{ fontFamily: "monospace" }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <ExportExcelButton
          rows={exportRows}
          columns={exportColumns}
          filename={exportFilename}
          disabled={!selectedRoleId}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!selectedRoleId || saving || !dirty || isInactive}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {isInactive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This role is inactive. Save will be disabled.
        </Alert>
      )}

      {!selectedRoleId ? (
        <Alert severity="info">Pick a role to view and edit its access.</Alert>
      ) : loadingMatrix ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : modules.length === 0 ? (
        <Alert severity="info">No active modules in the catalog yet.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 200 }}>
                  Module Key
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: 120 }}>
                  <Tooltip title="Toggle all modules">
                    <span>
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={toggleAll}
                        disabled={isInactive}
                        sx={{ p: 0.5 }}
                      />
                    </span>
                  </Tooltip>
                  Access
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.map((m) => (
                <TableRow
                  key={m.moduleId}
                  hover
                  onClick={() => toggleModule(m.moduleId)}
                  sx={{ cursor: isInactive ? "default" : "pointer" }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {m.moduleName}
                    </Typography>
                    {m.description && (
                      <Typography variant="caption" color="text.secondary">
                        {m.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {m.moduleKey}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={accessSet.has(m.moduleId)}
                      onChange={() => toggleModule(m.moduleId)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={isInactive}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolePermissionsMatrixTab;
